// src/utils/markdownToHtml.ts
import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import remarkRehype from "remark-rehype";
import rehypeRaw from "rehype-raw";
import rehypeStringify from "rehype-stringify";
import { createLogger } from "./logger";

const log = createLogger("markdownToHtml");

/**
 * Converts markdown text to HTML string using the unified processing pipeline
 *
 * This function uses the same markdown processing chain as our MarkdownRenderer component
 * to ensure consistent rendering behavior. It supports GitHub Flavored Markdown (GFM)
 * features including tables, strikethrough, task lists, and autolinks.
 *
 * @param markdown - The markdown text to convert
 * @returns Promise<string> - The HTML string representation
 *
 * @example
 * const html = await convertMarkdownToHtml("# Hello\n\nThis is **bold** text");
 * // Returns: "<h1>Hello</h1><p>This is <strong>bold</strong> text</p>"
 */
export async function convertMarkdownToHtml(markdown: string): Promise<string> {
  try {
    // Input validation
    if (!markdown || typeof markdown !== "string") {
      log.warn("Invalid markdown input provided", {
        type: typeof markdown,
        isEmpty: !markdown,
      });
      return "";
    }

    log.debug("Converting markdown to HTML", {
      markdownLength: markdown.length,
      markdownPreview: markdown.substring(0, 100) + (markdown.length > 100 ? "..." : ""),
    });

    // Process markdown through unified pipeline
    const file = await unified()
      .use(remarkParse) // Parse markdown to AST
      .use(remarkGfm) // Add GitHub Flavored Markdown support (tables, strikethrough, etc.)
      // Security Note: allowDangerousHtml is safe here because:
      // 1. Source is controlled: Markdown comes from our trusted backend AI agents
      // 2. Not user input: We don't process arbitrary markdown from untrusted sources
      // 3. Output destination: HTML goes to clipboard only, Word sanitizes on paste
      // 4. Agent control: Our backend validates and generates all content
      .use(remarkRehype, { allowDangerousHtml: true }) // Convert markdown AST to HTML AST
      .use(rehypeRaw) // Parse raw HTML embedded in markdown
      .use(rehypeStringify) // Convert HTML AST to string
      .process(markdown);

    const htmlString = String(file);

    log.debug("Successfully converted markdown to HTML", {
      markdownLength: markdown.length,
      htmlLength: htmlString.length,
      htmlPreview: htmlString.substring(0, 100) + (htmlString.length > 100 ? "..." : ""),
    });

    return htmlString;
  } catch (error) {
    // Log the error for debugging but don't throw - return original markdown as fallback
    log.error("Failed to convert markdown to HTML", {
      error: error instanceof Error ? error.message : String(error),
      errorStack: error instanceof Error ? error.stack : undefined,
      markdownLength: markdown.length,
    });

    // Return the original markdown as fallback
    // This ensures the copy operation doesn't fail completely
    log.info("Returning original markdown as fallback due to conversion error");
    return markdown;
  }
}
